import { Button, DatePicker, Input, Select, Space, Switch, Table, Tag } from 'antd';
import {
  EyeOutlined,
  EditTwoTone,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { MainLayout } from '../../components/Layout';
import {
  getCountOfNotifications,
  getManyNotifications,
  setNotificationIsReaded,
} from '../../utils/NoSqlUtils';
import moment from 'moment';
import { withRouter } from 'react-router-dom';

const { RangePicker } = DatePicker;
const { Option } = Select;

const DEFAULT_PAGE_SIZE = 10;

const NOTIFICATION_TYPE_OPTIONS = [
  'admin_notification',
  'expiry_period',
  'campaign_ended',
  'bundle_less',
  'NEW_AD',
  'USER_PAGE',
  'OTHER',
  'REJECT_AD',
  'APROVED_AD',
  'APPROVED_AD',
  'REACHED_100_AD',
  'REACHED_1000_AD',
  'rePostListing',
  'updateListing',
  'campaign',
];

const initialFilters = {
  adminOnly: true,
  searchText: '',
  isReaded: undefined,
  notificationTypes: [],
  dateRange: null,
  listingId: '',
  userId: '',
  topicId: '',
  notificationId: '',
  receiverId: '',
  title: '',
  body: '',
  mobileDeepLink: '',
  listingUrl: '',
  hasListingChanges: undefined,
  hasListingUrl: undefined,
};

const textFilter = (value) => ({ contains: value, mode: 'insensitive' });
const equalsFilter = (value) => ({ equals: value });
const notificationFilter = (filter) => ({ notifications: { is: filter } });

const ADMIN_ACTION_TYPE_FILTERS = {
  rePostListing: {
    OR: [
      notificationFilter({ notificationType: equalsFilter('rePostListing') }),
      notificationFilter({ headTitle: textFilter('إعادة نشر') }),
    ],
  },
  updateListing: {
    OR: [
      notificationFilter({ notificationType: equalsFilter('updateListing') }),
      notificationFilter({ headTitle: textFilter('تعديل الإعلان') }),
    ],
  },
  campaign: {
    OR: [
      notificationFilter({ notificationType: equalsFilter('campaign') }),
      notificationFilter({ headTitle: textFilter('تفعيل الإعلان') }),
    ],
  },
};

const getAdminActionType = (notification) => {
  const notificationType = notification && notification.notificationType;
  const headTitle = (notification && notification.headTitle) || '';

  if (notificationType === 'rePostListing') return 'rePostListing';
  if (notificationType === 'updateListing') return 'updateListing';
  if (notificationType === 'campaign') return 'campaign';
  if (notificationType !== 'admin_notification') return notificationType || '-';
  if (headTitle.indexOf('إعادة نشر') > -1) return 'rePostListing';
  if (headTitle.indexOf('تعديل الإعلان') > -1) return 'updateListing';
  if (headTitle.indexOf('تفعيل الإعلان') > -1) return 'campaign';

  return notificationType || '-';
};

const getTrimmedValue = (value) => {
  if (value === undefined || value === null) return '';
  return value.toString().trim();
};

const getNullableStringExistsFilter = (exists) => {
  if (exists === 'yes') {
    return { not: { equals: null } };
  }

  if (exists === 'no') {
    return { equals: null };
  }

  return undefined;
};

const buildNotificationsWhere = (filters) => {
  const where = {};
  const AND = [];

  if (filters.adminOnly) {
    where.isAdminNotification = equalsFilter(true);
  }

  if (filters.isReaded === 'read') {
    AND.push({ isReaded: equalsFilter(true) });
  }

  if (filters.isReaded === 'unread') {
    AND.push({ isReaded: equalsFilter(false) });
  }

  const searchText = getTrimmedValue(filters.searchText);
  if (searchText) {
    AND.push({
      OR: [
        { id: textFilter(searchText) },
        { notificationsId: textFilter(searchText) },
        { registeredUsersId: textFilter(searchText) },
        notificationFilter({ id: textFilter(searchText) }),
        notificationFilter({ headTitle: textFilter(searchText) }),
        notificationFilter({ oneSignalDescription: textFilter(searchText) }),
        notificationFilter({ notificationDescription: textFilter(searchText) }),
        notificationFilter({ listingId: textFilter(searchText) }),
        notificationFilter({ userId: textFilter(searchText) }),
        notificationFilter({ topicId: textFilter(searchText) }),
        notificationFilter({ listingUrl: textFilter(searchText) }),
        notificationFilter({ mobileDeepLink: textFilter(searchText) }),
      ],
    });
  }

  if (filters.notificationTypes && filters.notificationTypes.length) {
    const notificationTypes = filters.notificationTypes.filter(
      (type) => !ADMIN_ACTION_TYPE_FILTERS[type]
    );
    const adminActionFilters = filters.notificationTypes
      .filter((type) => ADMIN_ACTION_TYPE_FILTERS[type])
      .map((type) => ADMIN_ACTION_TYPE_FILTERS[type]);
    const typeFilters = [];

    if (notificationTypes.length) {
      typeFilters.push(
        notificationFilter({
          notificationType: { in: notificationTypes },
        })
      );
    }

    if (adminActionFilters.length) {
      typeFilters.push(...adminActionFilters);
    }

    if (typeFilters.length === 1) {
      AND.push(typeFilters[0]);
    } else if (typeFilters.length > 1) {
      AND.push({ OR: typeFilters });
    }
  }

  const title = getTrimmedValue(filters.title);
  if (title) {
    AND.push(notificationFilter({ headTitle: textFilter(title) }));
  }

  const body = getTrimmedValue(filters.body);
  if (body) {
    AND.push({
      OR: [
        notificationFilter({ oneSignalDescription: textFilter(body) }),
        notificationFilter({ notificationDescription: textFilter(body) }),
      ],
    });
  }

  const listingId = getTrimmedValue(filters.listingId);
  if (listingId) {
    AND.push(notificationFilter({ listingId: textFilter(listingId) }));
  }

  const userId = getTrimmedValue(filters.userId);
  if (userId) {
    AND.push({
      OR: [
        { registeredUsersId: textFilter(userId) },
        notificationFilter({ userId: textFilter(userId) }),
      ],
    });
  }

  const topicId = getTrimmedValue(filters.topicId);
  if (topicId) {
    AND.push(notificationFilter({ topicId: textFilter(topicId) }));
  }

  const notificationId = getTrimmedValue(filters.notificationId);
  if (notificationId) {
    AND.push({ notificationsId: textFilter(notificationId) });
  }

  const receiverId = getTrimmedValue(filters.receiverId);
  if (receiverId) {
    AND.push({ id: textFilter(receiverId) });
  }

  const mobileDeepLink = getTrimmedValue(filters.mobileDeepLink);
  if (mobileDeepLink) {
    AND.push(notificationFilter({ mobileDeepLink: textFilter(mobileDeepLink) }));
  }

  const listingUrl = getTrimmedValue(filters.listingUrl);
  if (listingUrl) {
    AND.push(notificationFilter({ listingUrl: textFilter(listingUrl) }));
  }

  if (filters.dateRange && filters.dateRange.length) {
    const [from, to] = filters.dateRange;
    const createdAt = {};

    if (from) {
      createdAt.gte = from.clone().startOf('day').toISOString();
    }

    if (to) {
      createdAt.lte = to.clone().endOf('day').toISOString();
    }

    if (Object.keys(createdAt).length) {
      AND.push(notificationFilter({ createdAt }));
    }
  }

  const listingChangesFilter = getNullableStringExistsFilter(
    filters.hasListingChanges
  );
  if (listingChangesFilter) {
    AND.push(notificationFilter({ listingChanges: listingChangesFilter }));
  }

  const listingUrlExistsFilter = getNullableStringExistsFilter(filters.hasListingUrl);
  if (listingUrlExistsFilter) {
    AND.push(notificationFilter({ listingUrl: listingUrlExistsFilter }));
  }

  if (AND.length) {
    where.AND = AND;
  }

  return where;
};

const mapNotificationReceivers = (receivers = []) => {
  return receivers.map(
    ({ id, isReaded, notificationsId, registeredUsersId, notifications }, i) => ({
      ...(notifications || {}),
      key: id || i,
      message: notifications && notifications.headTitle,
      createdAt: notifications && notifications.createdAt,
      isReaded,
      notificationId: id,
      notificationsId,
      registeredUsersId,
      userId: notifications && notifications.userId,
      adminActionType: getAdminActionType(notifications),
    })
  );
};

function NotificationsDetails({ history }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState(initialFilters);

  const fetchNotifications = async ({
    page = current,
    nextPageSize = pageSize,
    nextFilters = filters,
  } = {}) => {
    const where = buildNotificationsWhere(nextFilters);
    setLoading(true);

    try {
      const notificationsResult = await getManyNotifications({
        where,
        take: nextPageSize,
        skip: nextPageSize * (page - 1),
        orderBy: { id: 'desc' },
      });
      const countResult = await getCountOfNotifications({ where });

      const receivers =
        (notificationsResult &&
          notificationsResult.data &&
          notificationsResult.data.findManyNotificationReceivers) ||
        [];
      const totalCount =
        (countResult &&
          countResult.data &&
          countResult.data.notificationsOrRegisteredUsersCount &&
          countResult.data.notificationsOrRegisteredUsersCount._count &&
          countResult.data.notificationsOrRegisteredUsersCount._count.id) ||
        0;

      setNotifications(mapNotificationReceivers(receivers));
      setTotal(totalCount);
    } catch (e) {
      console.log('🚀 ~ file: NotificationsDetails/index.js ~ e:', e);
      setNotifications([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications({ page: 1 });
  }, []);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setCurrent(1);
    fetchNotifications({ page: 1 });
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setCurrent(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    fetchNotifications({
      page: 1,
      nextPageSize: DEFAULT_PAGE_SIZE,
      nextFilters: initialFilters,
    });
  };

  const markAsReadAndRefresh = async (notificationId) => {
    await setNotificationIsReaded({ notificationId });
    fetchNotifications({ page: current });
  };

  const columns = [
    {
      title: '',
      dataIndex: '',
      key: 'actions',
      width: 120,
      render: (text, record) => {
        return (
          <Space size="small">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              disabled={!record.listingUrl}
              onClick={async () => {
                await markAsReadAndRefresh(record.notificationId);
                window.open(record.listingUrl, '_blank');
              }}
            />

            <Button
              type="primary"
              icon={<EditTwoTone twoToneColor="white" />}
              size="small"
              disabled={!record.listingId}
              onClick={async () => {
                await markAsReadAndRefresh(record.notificationId);
                window.open(`/listing/edit/${record.listingId}`);
              }}
            />
            {record.listingChanges && (
              <Button
                type="dashed"
                icon={<UnorderedListOutlined />}
                size="small"
                onClick={async () => {
                  await markAsReadAndRefresh(record.notificationId);
                  history.push(`/notification/details/${record.notificationId}`);
                }}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (message, record) => (
        <div style={{ maxWidth: 360 }}>
          <div style={{ fontWeight: 600 }}>{message || '-'}</div>
          <div style={{ color: '#666', fontSize: 12 }}>
            {record.notificationDescription ||
              record.oneSignalDescription ||
              record.mobileDeepLink ||
              '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'notificationType',
      key: 'notificationType',
      width: 150,
      render: (notificationType, record) => record.adminActionType || notificationType || '-',
    },
    {
      title: 'Listing',
      dataIndex: 'listingId',
      key: 'listingId',
      width: 140,
      render: (listingId) => listingId || '-',
    },
    {
      title: 'User / Receiver',
      key: 'user',
      width: 190,
      render: (_, record) => (
        <div>
          <div>{record.userId || '-'}</div>
          <div style={{ color: '#888', fontSize: 12 }}>
            {record.registeredUsersId || record.notificationId}
          </div>
        </div>
      ),
    },
    {
      title: 'Topic',
      dataIndex: 'topicId',
      key: 'topicId',
      width: 150,
      render: (topicId) => topicId || '-',
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (createdAt) =>
        createdAt ? moment(createdAt).format('HH:mm - DD/MM/YY') : '-',
    },
    {
      title: 'Read',
      key: 'isReaded',
      dataIndex: 'isReaded',
      width: 120,
      render: (_, { isReaded }) => {
        return (
          <Tag color={isReaded ? 'geekblue' : 'volcano'}>
            {isReaded ? 'Read' : 'Unread'}
          </Tag>
        );
      },
    },
  ];

  const handleChange = async (pagination) => {
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);
    fetchNotifications({
      page: pagination.current,
      nextPageSize: pagination.pageSize,
    });
  };

  return (
    <MainLayout title="Notifications Details">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Input.Search
          allowClear
          placeholder="Search all fields"
          value={filters.searchText}
          onChange={(e) => updateFilter('searchText', e.target.value)}
          onSearch={applyFilters}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          mode="tags"
          placeholder="Notification types"
          value={filters.notificationTypes}
          onChange={(value) => updateFilter('notificationTypes', value)}
          style={{ width: 260 }}
        >
          {NOTIFICATION_TYPE_OPTIONS.map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder="Read status"
          value={filters.isReaded}
          onChange={(value) => updateFilter('isReaded', value)}
          style={{ width: 150 }}
        >
          <Option value="read">Read</Option>
          <Option value="unread">Unread</Option>
        </Select>
        <RangePicker
          value={filters.dateRange}
          onChange={(value) => updateFilter('dateRange', value)}
          style={{ width: 260 }}
        />
        <Input
          allowClear
          placeholder="Listing ID"
          value={filters.listingId}
          onChange={(e) => updateFilter('listingId', e.target.value)}
          style={{ width: 160 }}
        />
        <Input
          allowClear
          placeholder="User / receiver ID"
          value={filters.userId}
          onChange={(e) => updateFilter('userId', e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          allowClear
          placeholder="Topic ID"
          value={filters.topicId}
          onChange={(e) => updateFilter('topicId', e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          allowClear
          placeholder="Notification ID"
          value={filters.notificationId}
          onChange={(e) => updateFilter('notificationId', e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          allowClear
          placeholder="Receiver ID"
          value={filters.receiverId}
          onChange={(e) => updateFilter('receiverId', e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          allowClear
          placeholder="Title contains"
          value={filters.title}
          onChange={(e) => updateFilter('title', e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          allowClear
          placeholder="Body contains"
          value={filters.body}
          onChange={(e) => updateFilter('body', e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          allowClear
          placeholder="Mobile deep link"
          value={filters.mobileDeepLink}
          onChange={(e) => updateFilter('mobileDeepLink', e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          allowClear
          placeholder="Listing URL"
          value={filters.listingUrl}
          onChange={(e) => updateFilter('listingUrl', e.target.value)}
          style={{ width: 180 }}
        />
        <Select
          allowClear
          placeholder="Has changes"
          value={filters.hasListingChanges}
          onChange={(value) => updateFilter('hasListingChanges', value)}
          style={{ width: 150 }}
        >
          <Option value="yes">Has changes</Option>
          <Option value="no">No changes</Option>
        </Select>
        <Select
          allowClear
          placeholder="Has URL"
          value={filters.hasListingUrl}
          onChange={(value) => updateFilter('hasListingUrl', value)}
          style={{ width: 130 }}
        >
          <Option value="yes">Has URL</Option>
          <Option value="no">No URL</Option>
        </Select>
        <Space>
          <span>Admin only</span>
          <Switch
            checked={filters.adminOnly}
            onChange={(value) => updateFilter('adminOnly', value)}
          />
        </Space>
        <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>
          Search
        </Button>
        <Button onClick={resetFilters}>Reset</Button>
      </div>
      <Table
        dataSource={notifications}
        columns={columns}
        loading={loading}
        pagination={{
          showQuickJumper: false,
          showSizeChanger: true,
          current: current,
          total: total,
          pageSize: pageSize,
          pageSizeOptions: ['10', '20', '50', '100'],
          position: ['topLeft', 'bottomLeft'],
        }}
        onChange={handleChange}
        bordered
      />
    </MainLayout>
  );
}

export default withRouter(NotificationsDetails);
